import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { VideoEntry, SortOption, ViewMode } from '../types';

export const useVideoStore = defineStore('video', () => {
  const allVideos = ref<VideoEntry[]>([]);
  const filteredVideos = ref<VideoEntry[]>([]);
  const searchQuery = ref('');
  const sortBy = ref<SortOption>('name_asc');
  const viewMode = ref<ViewMode>((localStorage.getItem('videoView') as ViewMode) || 'grid');
  const currentPage = ref(1);
  const pageSize = 12;
  const loading = ref(false);
  const error = ref<string | null>(null);
  const selectedIds = ref<Set<string>>(new Set());

  const totalFiltered = computed(() => filteredVideos.value.length);
  const totalPages = computed(() => Math.max(1, Math.ceil(totalFiltered.value / pageSize)));
  const pagedVideos = computed(() => {
    const start = (currentPage.value - 1) * pageSize;
    return filteredVideos.value.slice(start, start + pageSize);
  });

  const selectedCount = computed(() => selectedIds.value.size);

  const hasSearch = computed(() => searchQuery.value.trim().length > 0);

  function applyFilterAndSort() {
    const keyword = searchQuery.value.trim().toLowerCase();
    const store = useVideoStore() as any;
    const groupFilter = store.activeGroupFilter as { id: string; type: string; fileIds?: string[] } | undefined;

    filteredVideos.value = allVideos.value.filter((v) => {
      // Custom group filter: filter by pre-loaded file IDs
      if (groupFilter?.type === 'custom' && groupFilter.fileIds) {
        if (!groupFilter.fileIds.includes(v.id)) return false;
      }

      // Type filter (auto-group): check MIME type
      if (groupFilter?.type === 'type') {
        const slug = groupFilter.id;
        const mime = v.mime_type || '';
        const ext = mime.split('/')[1] || '';
        if (ext.replace('-', '_') !== slug) return false;
      }

      if (!keyword) return true;
      return (v.name || '').toLowerCase().includes(keyword);
    });

    // 排序
    filteredVideos.value.sort((a, b) => {
      switch (sortBy.value) {
        case 'name_asc':
          return (a.name || '').localeCompare(b.name || '');
        case 'name_desc':
          return (b.name || '').localeCompare(a.name || '');
        case 'size_desc':
          return (b.size || 0) - (a.size || 0);
        case 'size_asc':
          return (a.size || 0) - (b.size || 0);
        default:
          return 0;
      }
    });

    // 重置到第 1 页
    currentPage.value = 1;
  }

  function setSearch(query: string) {
    searchQuery.value = query;
    if (!query.trim()) {
      const store = useVideoStore() as any;
      store.activeGroupFilter = undefined;
    }
    applyFilterAndSort();
  }

  function setSort(option: SortOption) {
    sortBy.value = option;
    applyFilterAndSort();
  }

  function setView(mode: ViewMode) {
    viewMode.value = mode;
    localStorage.setItem('videoView', mode);
  }

  function goPage(page: number) {
    currentPage.value = page;
  }

  function highlightDuplicates() {
    const nameCount = new Map<string, VideoEntry[]>();
    for (const v of allVideos.value) {
      const key = v.name.toLowerCase();
      if (!nameCount.has(key)) nameCount.set(key, []);
      nameCount.get(key)!.push(v);
    }

    for (const [, group] of nameCount) {
      if (group.length <= 1) continue;
      const sizeCount = new Map<number, VideoEntry[]>();
      for (const v of group) {
        if (!sizeCount.has(v.size)) sizeCount.set(v.size, []);
        sizeCount.get(v.size)!.push(v);
      }
      for (const [, duplicates] of sizeCount) {
        if (duplicates.length > 1) {
          duplicates.forEach((v) => { v.isDuplicate = true; });
        }
      }
    }
  }

  function removeVideo(fileId: string) {
    allVideos.value = allVideos.value.filter((v) => v.id !== fileId);
    selectedIds.value.delete(fileId);
    applyFilterAndSort();
  }

  function toggleSelect(id: string) {
    const s = selectedIds.value;
    if (s.has(id)) s.delete(id); else s.add(id);
  }

  function toggleSelectAll(currentPageIds: VideoEntry[]) {
    const s = selectedIds.value;
    const allSelected = currentPageIds.every((v) => s.has(v.id));
    for (const v of currentPageIds) {
      if (allSelected) s.delete(v.id); else s.add(v.id);
    }
  }

  function clearSelection() {
    selectedIds.value.clear();
  }

  return {
    allVideos,
    filteredVideos,
    searchQuery,
    sortBy,
    viewMode,
    currentPage,
    pageSize,
    loading,
    error,
    totalFiltered,
    totalPages,
    pagedVideos,
    hasSearch,
    selectedIds,
    selectedCount,
    setSearch,
    setSort,
    setView,
    goPage,
    highlightDuplicates,
    removeVideo,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    applyFilterAndSort,
  };
});
